import React, { useMemo, useState } from "react";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import apiUrl from "../Apiurl";
import stilePulsanti from "../interfaccia/stilePulsanti";

const PREVIEW_ROWS = 20;
const DETAILS_MAX_ROWS = 2000;
const BASIC_COLUMNS = ["codVS", "ean"];

function escapeCsvCell(value) {
  const normalized = String(value ?? "");
  if (/[",\r\n]/.test(normalized)) {
    return `"${normalized.replace(/"/g, '""')}"`;
  }
  return normalized;
}

function buildCsv(rows, columns) {
  const lines = [
    columns.map(escapeCsvCell).join(","),
    ...rows.map((row) =>
      columns.map((column) => escapeCsvCell(row[column])).join(","),
    ),
  ];
  return `\uFEFF${lines.join("\r\n")}\r\n`;
}

function downloadRows(rows, columns, filenamePrefix) {
  const blob = new Blob([buildCsv(rows, columns)], {
    type: "text/csv;charset=utf-8",
  });
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = `${filenamePrefix}-${new Date()
    .toISOString()
    .slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
}

function formatNumber(value) {
  return Number(value ?? 0).toLocaleString("it-IT");
}

function EanToCodvsPage({ utente }) {
  const { getAccessTokenSilently } = useAuth0();
  const { buttonClassesGreen, buttonClassesWhite } = stilePulsanti;
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState("");
  const [detailsMessage, setDetailsMessage] = useState("");
  const [result, setResult] = useState(null);

  const permissionsLoaded = Boolean(utente?.permessiAPI);
  const canUse = permissionsLoaded
    ? Boolean(utente?.permessiAPI?.["read:debug"])
    : true;
  const preview = useMemo(
    () => (Array.isArray(result?.rows) ? result.rows.slice(0, PREVIEW_ROWS) : []),
    [result],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!file || loading) {
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const accessToken = await getAccessTokenSilently();
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${apiUrl}/manutenzione/ean-to-codvs`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      const rows = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      const nextResult = {
        rows,
        meta: response?.data?.meta ?? {},
      };
      setResult(nextResult);
      downloadRows(rows, BASIC_COLUMNS, "ean-to-codvs");
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error ??
          requestError?.message ??
          "Impossibile elaborare il file CSV.",
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadModaDetails = async () => {
    if (!result?.rows?.length || detailsLoading) {
      return;
    }

    setDetailsLoading(true);
    setError("");
    setDetailsMessage("");

    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.post(
        `${apiUrl}/manutenzione/ean-to-codvs/details`,
        { rows: result.rows },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const rows = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      const columns = Array.isArray(response?.data?.meta?.columns)
        ? response.data.meta.columns
        : [];

      if (!columns.length) {
        throw new Error("Il backend non ha restituito le colonne dell'export.");
      }

      downloadRows(rows, columns, "ean-to-codvs-dettaglio-moda");
      setDetailsMessage(
        `Scaricato il dettaglio Moda per ${formatNumber(rows.length)} codici VS.`,
      );
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error ??
          requestError?.message ??
          "Impossibile recuperare il dettaglio articoli da Moda.",
      );
    } finally {
      setDetailsLoading(false);
    }
  };

  if (!canUse) {
    return (
      <div className="areaComposizione fr colonnaTuttaPagina">
        <section className="sfondoBiancoTema br4 shadow-4 pa4">
          <h1 className="mt0">EAN to CODVS</h1>
          <p>Non hai il permesso read:debug necessario per usare questo tool.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="areaComposizione fr colonnaTuttaPagina">
      <section className="sfondoBiancoTema br4 shadow-4 pa4 pa5-ns">
        <div className="mw8 center">
          <h1 className="mt0 mb2">EAN to CODVS</h1>
          <p className="mt0 lh-copy">
            Carica un CSV contenente i barcode. Il file restituito contiene una
            riga per ogni codice VS trovato e uno degli EAN corrispondenti
            presenti nel file originale.
          </p>

          <div className="ba bordoColScrittaDef-10 br3 pa3 pa4-ns mv4">
            <h2 className="f4 mt0">Formato accettato</h2>
            <p className="lh-copy mb2">
              È sufficiente un barcode per riga. Puoi usare anche
              un'intestazione <code>ean</code> o <code>barcode</code>; sono
              accettati CSV separati da virgola o punto e virgola.
            </p>
            <pre className="sfondoColChiaro pa3 br2 overflow-auto mb0">
              {"ean\n1111\n2222\n3333"}
            </pre>
          </div>

          <form onSubmit={handleSubmit}>
            <label className="db b mb2" htmlFor="ean-to-codvs-file">
              File CSV
            </label>
            <input
              id="ean-to-codvs-file"
              className="db w-100 ba bordoColScrittaDef-10 pa3 br2 mb3"
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
                setResult(null);
                setError("");
                setDetailsMessage("");
              }}
            />
            <button
              className={buttonClassesGreen}
              type="submit"
              disabled={!file || loading}
            >
              {loading ? "Elaborazione in corso…" : "Elabora e scarica CSV"}
            </button>
          </form>

          {error ? (
            <div className="ba b--dark-red dark-red br2 pa3 mt4" role="alert">
              {error}
            </div>
          ) : null}

          {result ? (
            <div className="mt4" aria-live="polite">
              <h2 className="f4 mb3">Elaborazione completata</h2>
              <div className="flex flex-wrap nl2 nr2">
                <div className="pa2 w-50-m w-25-l">
                  <div className="ba bordoColScrittaDef-10 br3 pa3 h-100">
                    <div className="f3 b">{formatNumber(result.meta.inputRows)}</div>
                    <div>righe lette</div>
                  </div>
                </div>
                <div className="pa2 w-50-m w-25-l">
                  <div className="ba bordoColScrittaDef-10 br3 pa3 h-100">
                    <div className="f3 b">{formatNumber(result.meta.matchedEans)}</div>
                    <div>EAN associati</div>
                  </div>
                </div>
                <div className="pa2 w-50-m w-25-l">
                  <div className="ba bordoColScrittaDef-10 br3 pa3 h-100">
                    <div className="f3 b">{formatNumber(result.meta.codiciVs)}</div>
                    <div>codici VS esportati</div>
                  </div>
                </div>
                <div className="pa2 w-50-m w-25-l">
                  <div className="ba bordoColScrittaDef-10 br3 pa3 h-100">
                    <div className="f3 b">{formatNumber(result.meta.unmatchedEans)}</div>
                    <div>EAN non trovati</div>
                  </div>
                </div>
              </div>

              {Number(result.meta.invalidEans ?? 0) > 0 ? (
                <p className="dark-orange">
                  Ignorati {formatNumber(result.meta.invalidEans)} valori più
                  lunghi dei 20 caratteri previsti da vs_barcode.
                </p>
              ) : null}

              <button
                className={`${buttonClassesWhite} mt2`}
                type="button"
                onClick={() =>
                  downloadRows(result.rows, BASIC_COLUMNS, "ean-to-codvs")
                }
              >
                Scarica codVS + EAN
              </button>

              <button
                className={`${buttonClassesGreen} mt2`}
                type="button"
                disabled={
                  detailsLoading || result.rows.length > DETAILS_MAX_ROWS
                }
                onClick={downloadModaDetails}
              >
                {detailsLoading
                  ? "Recupero dati Moda…"
                  : "Scarica dettaglio Moda"}
              </button>

              <p className="f6 lh-copy">
                Il dettaglio Moda contiene una riga per codice VS. Usa l'EAN
                selezionato per individuare taglia e colore e include prezzi,
                classificazioni, brand, stagione e fornitore.
              </p>

              {result.rows.length > DETAILS_MAX_ROWS ? (
                <p className="dark-orange">
                  Il dettaglio Moda è disponibile per un massimo di{" "}
                  {formatNumber(DETAILS_MAX_ROWS)} codici VS per volta; dividi
                  il file di origine in più parti.
                </p>
              ) : null}

              {detailsMessage ? (
                <p className="dark-green b" role="status">
                  {detailsMessage}
                </p>
              ) : null}

              {preview.length ? (
                <div className="overflow-auto mt4">
                  <h3 className="f5">
                    Anteprima ({Math.min(result.rows.length, PREVIEW_ROWS)} di{" "}
                    {formatNumber(result.rows.length)})
                  </h3>
                  <table className="collapse w-100">
                    <thead>
                      <tr className="tl sfondoColChiaro">
                        <th className="pa2 ba bordoColScrittaDef-10">codVS</th>
                        <th className="pa2 ba bordoColScrittaDef-10">ean</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((row) => (
                        <tr key={`${row.codVS}-${row.ean}`}>
                          <td className="pa2 ba bordoColScrittaDef-10">
                            {row.codVS}
                          </td>
                          <td className="pa2 ba bordoColScrittaDef-10">
                            {row.ean}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>Nessun codice VS associato agli EAN caricati.</p>
              )}
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}

export default EanToCodvsPage;
